import { useMemo } from 'react'
import { Alert, Card, Empty, Skeleton, Space, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePermissions } from '@/shared/auth/usePermissions'
import { todayInTenantZone } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { fetchAgentsReport } from '@/features/reports/reportsApi'

const ROWS = 5

/**
 * Bugungi sotuv bo'yicha agentlar.
 *
 * <h2>Nega bosh sahifada</h2>
 * "Bugungi daromad" bitta son va u savolni tug'diradi: KIM sotdi.
 * Javob allaqachon bor edi (`GET /api/reports/agents`), lekin faqat
 * Hisobotlar sahifasida, sana oynasini qo'lda tanlagandan keyin.
 *
 * <h2>⚠️ Sotuvi NOL bo'lganlar chiqarilmaydi</h2>
 * Ro'yxatning maqsadi — "bugun kim ishladi", "kim ro'yxatda bor" emas.
 * Nol qatorlar reytingni o'qib bo'lmaydigan qiladi (Hisobotlar
 * sahifasida bu 60 qator degani edi).
 */
export function TopAgentsPanel() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const allowed = can('report.read') && can('agent.read')

  // ⚠️ Oyna — BUGUN, TENANT zonasidagi kalendar kuni. Server `from`/`to`
  // ni `YYYY-MM-DD` kutadi va zonani o'zi qo'llaydi (`reportsApi`).
  //
  // ⚠️ Ilgari bu yerda `dayjs().format(ISO_DAY)` turardi — ya'ni BRAUZER
  // kuni. Izoh "tenant zonasidagi kalendar kuni" deb yozilgan, kod esa
  // boshqasini qilardi: Toshkentdan boshqarilayotgan Kobul tenantida
  // kuniga yarim soat davomida panel ERTANGI kunning sotuvini so'rardi
  // va "Bugungi sotuv: 0" ko'rsatardi.
  const range = useMemo(() => {
    const today = todayInTenantZone()
    return { from: today, to: today }
  }, [])

  const agents = useQuery({
    queryKey: queryKeys.reports.agents(range.from, range.to, ROWS * 4),
    queryFn: () => fetchAgentsReport(range, ROWS * 4),
    enabled: allowed,
    refetchInterval: 60_000,
  })

  const rows = useMemo(
    () =>
      (agents.data ?? [])
        .filter((a) => a.sold.amount > 0)
        .sort((a, b) => b.sold.amount - a.sold.amount)
        .slice(0, ROWS),
    [agents.data],
  )

  if (!allowed) return null

  return (
    <Card
      title={t('dashboard.topAgents.title')}
      extra={<Link to="/reports">{t('dashboard.openReports')}</Link>}
      size="small"
      style={{ height: '100%' }}
    >
      {agents.isError ? (
        <Alert type="error" showIcon message={translateError(agents.error, t)} />
      ) : agents.isPending ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : rows.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('dashboard.topAgents.empty')} />
      ) : (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {rows.map((agent) => (
            <div
              key={agent.agentId}
              style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}
            >
              <Typography.Text ellipsis style={{ maxWidth: 150 }}>
                {agent.displayName}
              </Typography.Text>
              <Space size={8}>
                <Typography.Text strong style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                  {formatMoneyValue(agent.sold)}
                </Typography.Text>
                {/* Komissiya — agentning O'Z ulushi, sotuvdan alohida ustun */}
                <Typography.Text type="secondary" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                  {formatMoneyValue(agent.commission)}
                </Typography.Text>
              </Space>
            </div>
          ))}
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {t('dashboard.topAgents.hint')}
          </Typography.Text>
        </Space>
      )}
    </Card>
  )
}
